import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ChildTableStateService } from './child-table-state.service';
import { ChildTableA11yService } from './child-table-a11y.service';

@Injectable()
export class ChildTableDndService<T = any> {
  draggingRowId: string | null = null;
  dragOverRowId: string | null = null;
  private dragSourceIndex = -1;

  /** Emits { fromIndex, toIndex } on successful drop reorder. */
  readonly reorder$ = new Subject<{ fromIndex: number; toIndex: number }>();

  rowReorderable = false;
  readonly = false;
  pasteErrorLocked = false;

  constructor(
    private readonly stateService: ChildTableStateService<T>,
    private readonly a11yService: ChildTableA11yService,
  ) {}

  onDragStart(event: DragEvent, rowId: string, rowIndex: number): void {
    if (this.pasteErrorLocked || !this.rowReorderable || this.readonly) {
      event.preventDefault();
      return;
    }
    this.draggingRowId = rowId;
    this.dragSourceIndex = rowIndex;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', rowId);
      const ghost = document.createElement('div');
      ghost.className = 'drag-ghost';
      ghost.textContent = `Row ${rowIndex + 1}`;
      ghost.style.cssText = 'position:absolute;left:-9999px;padding:8px 12px;background:#4b3fff;color:white;border-radius:4px;font-size:14px;';
      document.body.appendChild(ghost);
      event.dataTransfer.setDragImage(ghost, 0, 0);
      setTimeout(() => ghost.remove(), 0);
    }
    this.a11yService.announce(`Dragging row ${rowIndex + 1}`);
  }

  onDragEnd(event: DragEvent): void {
    this.draggingRowId = null;
    this.dragOverRowId = null;
    this.dragSourceIndex = -1;
  }

  onDragOver(event: DragEvent): void {
    if (!this.rowReorderable || this.readonly) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onDragEnter(event: DragEvent, rowId: string): void {
    if (!this.rowReorderable || this.readonly) return;
    if (rowId === this.draggingRowId) return;
    event.preventDefault();
    this.dragOverRowId = rowId;
  }

  onDragLeave(event: DragEvent): void {
    // Intentionally minimal to prevent flicker
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    if (!this.rowReorderable || this.readonly || this.dragSourceIndex < 0 || !this.draggingRowId) return;
    const targetRow = (event.target as HTMLElement).closest('.grid-row') as HTMLElement | null;
    if (!targetRow) return;
    const targetIndexAttr = targetRow.dataset['rowIndex'];
    if (!targetIndexAttr) return;
    const targetIndex = Number.parseInt(targetIndexAttr, 10);
    if (this.dragSourceIndex !== targetIndex) {
      this.stateService.moveRow(this.dragSourceIndex, targetIndex);
      this.a11yService.announceRowMoved(this.dragSourceIndex + 1, targetIndex + 1);
      this.reorder$.next({ fromIndex: this.dragSourceIndex, toIndex: targetIndex });
    }
    this.onDragEnd(event);
  }
}
